import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Search, AlertTriangle, CheckCircle, Loader, ArrowRight, TrendingUp, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

const RISK_STYLE = {
  Low:    { border: 'border-green-500/25',  badge: 'bg-green-500/15 text-green-400 border border-green-500/25' },
  Medium: { border: 'border-yellow-500/25', badge: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/25' },
  High:   { border: 'border-red-500/25',    badge: 'bg-red-500/15 text-red-400 border border-red-500/25' },
};

export default function WalletScannerPage() {
  const { token, user, API_URL } = useAuth();
  const [wallet,  setWallet]  = useState('');
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [limitInfo, setLimitInfo] = useState(null); // { used, limit, plan }

  const handleScan = async () => {
    const addr = wallet.trim();
    if (!addr) return;
    if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
      setError('Invalid Ethereum address — must start with 0x and be 42 characters.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setLimitInfo(null);

    try {
      const r = await fetch(`${API_URL}/api/scan/${addr}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await r.json();

      if (r.status === 429 && data.limitReached) {
        // Daily limit hit
        setLimitInfo({ used: data.used, limit: data.limit, plan: data.plan });
        setError(data.error);
        return;
      }

      if (!r.ok) throw new Error(data.error || 'Scan failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const rs = result ? (RISK_STYLE[result.riskLevel] || RISK_STYLE.Low) : null;
  const plan = user?.plan || 'free';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Wallet Scanner</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Analyze any Ethereum wallet for security risks and suspicious activity
        </p>
      </div>

      {/* Input */}
      <div className="glass-card p-5 mb-5">
        <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
          Ethereum Wallet Address
        </label>
        <div className="flex gap-3">
          <input
            type="text"
            value={wallet}
            onChange={e => { setWallet(e.target.value); setError(''); setLimitInfo(null); }}
            onKeyDown={e => e.key === 'Enter' && handleScan()}
            placeholder="0x742d35Cc6634C0532925a3b8D4C9D7F..."
            className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-700 font-mono focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.06] transition-all"
          />
          <button
            onClick={handleScan}
            disabled={loading || !wallet.trim()}
            className="flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-sm px-6 py-3 rounded-xl transition-all flex-shrink-0"
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
            {loading ? 'Scanning…' : 'Scan'}
          </button>
        </div>

        {/* Error / limit warning */}
        {error && (
          <div className={`mt-3 rounded-xl px-4 py-3 text-sm border ${
            limitInfo
              ? 'bg-amber-500/10 border-amber-500/25 text-amber-400'
              : 'bg-red-500/10 border-red-500/20 text-red-400'
          }`}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p>{error}</p>
                {limitInfo && (
                  <Link
                    to="/dashboard/billing"
                    className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-[#00E5FF] hover:underline"
                  >
                    Upgrade your plan <ArrowRight size={11} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Plan usage hint (only when logged in and no error) */}
        {token && !error && !loading && (
          <p className="mt-2 text-[11px] text-slate-700">
            {plan === 'free'
              ? 'Free plan: 10 scans per day. '
              : plan === 'starter'
              ? 'Starter plan: 200 scans per day. '
              : plan === 'pro'
              ? 'Pro plan: 1,000 scans per day. '
              : 'Enterprise plan: unlimited scans. '}
            <Link to="/dashboard/billing" className="text-[#00E5FF]/60 hover:text-[#00E5FF] transition-colors">
              Change plan
            </Link>
          </p>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">

          {/* Risk summary card */}
          <div className={`glass-card p-5 border ${rs.border}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-mono text-xs text-slate-600 mb-2 break-all">{result.wallet}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${rs.badge}`}>
                    {result.riskLevel} Risk
                  </span>
                  <span className="text-slate-500 text-sm">
                    Score:{' '}
                    <span className="text-white font-bold tabular-nums">{result.riskScore}/100</span>
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-slate-600 mb-0.5">Reputation</div>
                <div className="text-3xl font-bold text-[#00E5FF] tabular-nums">{result.reputationScore}</div>
                <div className="text-xs text-slate-600">/100</div>
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'ETH Balance',     value: `${result.balanceETH} ETH` },
              { label: 'Transactions',    value: result.txCount?.toLocaleString() },
              { label: 'Token Transfers', value: result.tokenTransfers?.toLocaleString() },
              { label: 'Contract Calls',  value: result.contractCalls?.toLocaleString() },
            ].map(({ label, value }) => (
              <div key={label} className="glass-card p-4 text-center border border-white/[0.05]">
                <div className="text-lg font-bold text-white tabular-nums">{value ?? '—'}</div>
                <div className="text-[11px] text-slate-600 mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Warnings */}
          {result.warnings?.length > 0 ? (
            <div className="glass-card p-4 border border-red-500/20">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-red-400">
                  Security Warnings ({result.warnings.length})
                </span>
              </div>
              <ul className="space-y-2">
                {result.warnings.map((w, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <ArrowRight size={12} className="text-red-500 flex-shrink-0" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="glass-card p-4 border border-green-500/20 flex items-center gap-3">
              <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
              <span className="text-sm text-green-400">No security warnings detected.</span>
            </div>
          )}

          {/* Etherscan link */}
          <div className="flex justify-end">
            <a
              href={`https://etherscan.io/address/${result.wallet}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#00E5FF] transition-colors"
            >
              <LinkIcon size={11} />
              View on Etherscan
            </a>
          </div>

          {/* Recent transactions */}
          {result.transactions?.length > 0 && (
            <div className="glass-card p-5">
              <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                <TrendingUp size={13} className="text-[#00E5FF]" />
                Recent Transactions
              </h3>
              <div className="space-y-2">
                {result.transactions.map((tx, i) => (
                  <div key={i} className="bg-white/[0.03] rounded-lg px-3 py-2.5 text-xs font-mono border border-white/[0.04]">
                    <div className="flex items-center gap-2 text-slate-500 flex-wrap">
                      <span className="text-emerald-500/70">from</span>
                      <span className="text-slate-400">{tx.from?.slice(0, 10)}…{tx.from?.slice(-6)}</span>
                      <span className="text-slate-700">→</span>
                      <span className="text-[#00E5FF]/60">to</span>
                      <span className="text-slate-400">{tx.to?.slice(0, 10)}…{tx.to?.slice(-6)}</span>
                      <span className="ml-auto text-slate-500 tabular-nums">{tx.value} ETH</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
